import { useEffect, useRef, useState } from 'react';
import { Button, Panel } from '../ui';

/**
 * Game control — session:control { command: pause | unpause | stop }.
 * The relay forwards it; whether the game acts on it is up to the game (egdl2 raises
 * OnRemotePause/Unpause/Stop but nothing subscribes yet, and it never reports 'paused').
 * So the card only claims what it knows: "sent", then whatever status the game reports back.
 */
const CONFIRM_MS = 6000;
const CONFIRM_WAIT = 5000;

export default function GameControlCard({ status, connected, send }) {
    const [pending, setPending] = useState(null); // { command, at, statusAt }
    const [unconfirmed, setUnconfirmed] = useState(null);
    const [armed, setArmed] = useState(false);
    const timer = useRef(null);
    const armTimer = useRef(null);

    const paused = status === 'paused';
    const ended = status === 'ended';
    const signal = connected && status !== 'disconnected';

    // Clear "pending" once the game reports the state we asked for.
    useEffect(() => {
        if (!pending) return;
        const want = { pause: 'paused', unpause: 'playing', stop: 'ended' }[pending.command];
        if (status === want) {
            setPending(null);
            setUnconfirmed(null);
            clearTimeout(timer.current);
        }
    }, [status, pending]);

    useEffect(() => () => { clearTimeout(timer.current); clearTimeout(armTimer.current); }, []);

    const fire = (command) => {
        if (!send(command)) return;
        setUnconfirmed(null);
        setPending({ command, at: Date.now() });
        clearTimeout(timer.current);
        timer.current = setTimeout(() => {
            setPending(null);
            setUnconfirmed(command);
        }, CONFIRM_WAIT);
    };

    const onEnd = () => {
        if (!armed) {
            setArmed(true);
            clearTimeout(armTimer.current);
            armTimer.current = setTimeout(() => setArmed(false), CONFIRM_MS);
            return;
        }
        setArmed(false);
        fire('stop');
    };

    const stateLabel = !signal ? 'No signal' : ended ? 'Ended' : paused ? 'Paused' : 'Running';
    const dot = !signal ? 'var(--egdl-danger)' : paused || ended ? 'var(--egdl-warning)' : 'var(--egdl-success)';
    const sentCopy = { pause: 'Pause sent — waiting for the game', unpause: 'Resume sent — waiting for the game', stop: 'End sent — waiting for the game' };
    const noCopy = { pause: 'The game didn’t confirm the pause.', unpause: 'The game didn’t confirm the resume.', stop: 'The game didn’t confirm the end.' };

    return (
        <Panel
            title="Game control"
            right={(
                <span className="eg-status" style={{ color: 'var(--egdl-text-tertiary)' }}>
                    <span className="eg-dot" style={{ background: dot }} />
                    <span>{stateLabel}</span>
                </span>
            )}
        >
            <div className="ls-control">
                <Button variant="primary" size="xl" icon={paused ? 'play' : 'pause'} iconSize={22} disabled={!signal || ended} onClick={() => fire(paused ? 'unpause' : 'pause')}>
                    {paused ? 'Resume game' : 'Pause game'}
                </Button>
                <Button variant="danger" disabled={!signal || ended} onClick={onEnd} aria-live="polite" style={armed ? { background: 'var(--egdl-danger)', color: 'var(--egdl-background)' } : undefined}>
                    {armed ? 'Tap again to end the game' : 'End game'}
                </Button>
                {pending ? <span className="ls-caption" role="status">{sentCopy[pending.command]}</span> : null}
                {!pending && unconfirmed ? <span className="ls-caption ls-caption--warning" role="status">{noCopy[unconfirmed]}</span> : null}
            </div>
        </Panel>
    );
}
